// Locale-independent normalizers for the two hand-rolled input components
// (TimeInput24h, DateInputISO). SportAdmin renders all clock times as 24h
// `HH:MM` and all dates as ISO `YYYY-MM-DD` regardless of browser locale
// (see CLAUDE.md), so these helpers are the single source of truth for
// coercing loose user input into the canonical stored form. Kept in a
// component-free module so they can be unit-tested as pure functions.

/** Matches a canonical 24h time-of-day, "00:00" through "23:59". */
export const TIME_HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Matches a canonical ISO date, "YYYY-MM-DD" with a real month/day range. */
export const DATE_ISO = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

// normalizeTime24h coerces loose input toward canonical "HH:MM":
//   - "" and whitespace-only → "" (empty is a valid, cleared value)
//   - "9:30" → "09:30", "9:3" → "09:03" (pad each half of an H:M pair)
// Anything that doesn't land on a valid 24h time is returned trimmed but
// otherwise untouched, so the surrounding form's validation can surface it.
export function normalizeTime24h(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === '') return '';
  // Only pad when there's a colon — a bare number is incomplete input the
  // user is still editing, not something to reshape.
  const m = trimmed.match(/^(\d{1,2}):(\d{1,2})$/);
  if (m && m[1] !== undefined && m[2] !== undefined) {
    const candidate = `${m[1].padStart(2, '0')}:${m[2].padStart(2, '0')}`;
    if (TIME_HHMM.test(candidate)) return candidate;
  }
  return trimmed;
}

// normalizeDateISO coerces loose input toward canonical "YYYY-MM-DD":
//   - "" and whitespace-only → ""
//   - "19900526" → "1990-05-26" (8 straight digits, e.g. an Excel paste)
//   - "2026-5-3" → "2026-05-03" (pad short month/day)
// Anything that doesn't land on a valid ISO date is returned trimmed but
// otherwise untouched, for form-level validation to catch.
export function normalizeDateISO(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === '') return '';
  if (/^\d{8}$/.test(trimmed)) {
    const candidate = `${trimmed.slice(0, 4)}-${trimmed.slice(4, 6)}-${trimmed.slice(6, 8)}`;
    if (DATE_ISO.test(candidate)) return candidate;
  }
  const m = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m && m[1] !== undefined && m[2] !== undefined && m[3] !== undefined) {
    const candidate = `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
    if (DATE_ISO.test(candidate)) return candidate;
  }
  return trimmed;
}
